import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import { getSubscriptionConfig, getSubscriptionPricing } from '../api';
import {
    getPrice,
    getPriceItem,
    parsePricingResponse,
} from '../utils/devicePricing';
import { SUBSCRIPTION_PERIOD_DAYS } from '../utils/subscription';

const PricingContext = createContext(null);

let pricingPromise = null;
let configPromise = null;

function loadPricing() {
    if (!pricingPromise) {
        pricingPromise = getSubscriptionPricing().catch((err) => {
            pricingPromise = null;
            throw err;
        });
    }
    return pricingPromise;
}

function loadConfig() {
    if (!configPromise) {
        configPromise = getSubscriptionConfig().catch((err) => {
            configPromise = null;
            throw err;
        });
    }
    return configPromise;
}

export function resetPricingLoad() {
    pricingPromise = null;
    configPromise = null;
}

export function PricingProvider({ children }) {
    const [pricing, setPricing] = useState(null);
    const [parsedPricing, setParsedPricing] = useState(null);
    const [periodDays, setPeriodDays] = useState(SUBSCRIPTION_PERIOD_DAYS);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const applyPricingResponse = useCallback((data) => {
        const parsed = parsePricingResponse(data);
        setPricing(data);
        setParsedPricing(parsed);
        setError('');
        return parsed;
    }, []);

    const applyConfigResponse = useCallback((data) => {
        const days = Number(data?.period_days);
        if (Number.isInteger(days) && days > 0) {
            setPeriodDays(days);
        }
        return days;
    }, []);

    const ensurePricing = useCallback(async () => {
        if (parsedPricing) {
            return parsedPricing;
        }

        setIsLoading(true);
        setError('');

        try {
            const [pricingData, configData] = await Promise.all([
                loadPricing(),
                loadConfig(),
            ]);
            applyConfigResponse(configData);
            return applyPricingResponse(pricingData);
        } catch (err) {
            const message = err.message || 'Не удалось загрузить цены';
            setError(message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [parsedPricing, applyPricingResponse, applyConfigResponse]);

    const retryPricing = useCallback(async () => {
        resetPricingLoad();
        setPricing(null);
        setParsedPricing(null);
        setError('');
        setIsLoading(true);

        try {
            const [pricingData, configData] = await Promise.all([
                loadPricing(),
                loadConfig(),
            ]);
            applyConfigResponse(configData);
            return applyPricingResponse(pricingData);
        } catch (err) {
            const message = err.message || 'Не удалось загрузить цены';
            setError(message);
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [applyPricingResponse, applyConfigResponse]);

    const deviceOptions = useMemo(
        () => parsedPricing?.options ?? [],
        [parsedPricing],
    );

    const getMonthlyPrice = useCallback((devices) => {
        if (!parsedPricing) {
            return null;
        }
        return getPrice(devices, parsedPricing);
    }, [parsedPricing]);

    const getPriceItemForDevices = useCallback((devices) => {
        if (!parsedPricing) {
            return null;
        }
        return getPriceItem(devices, parsedPricing);
    }, [parsedPricing]);

    const value = useMemo(() => ({
        pricing,
        parsedPricing,
        deviceOptions,
        periodDays,
        isLoading,
        error,
        ensurePricing,
        retryPricing,
        getPrice: getMonthlyPrice,
        getPriceItem: getPriceItemForDevices,
    }), [
        pricing,
        parsedPricing,
        deviceOptions,
        periodDays,
        isLoading,
        error,
        ensurePricing,
        retryPricing,
        getMonthlyPrice,
        getPriceItemForDevices,
    ]);

    return (
        <PricingContext.Provider value={value}>
            {children}
        </PricingContext.Provider>
    );
}

export function usePricing() {
    const context = useContext(PricingContext);
    if (!context) {
        throw new Error('usePricing must be used within PricingProvider');
    }
    return context;
}
